import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Clipboard from "expo-clipboard";
import React, {
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { Platform, StyleSheet, View, useWindowDimensions } from "react-native";
import { Input, useTheme } from "react-native-elements";
import { hasFlag } from "../backend/Flags";
import {
  PLAYER_INITIAL_STATE,
  PlayerContext,
  PlayerContextValue,
} from "../backend/PlayerContext";
import { serializeSaveData } from "../backend/SaveData";
import { EventTag } from "../backend/events/GameEvent";
import {
  SpellElement,
  getUnlockedElements,
} from "../backend/spells/ElementsAndSchools";
import ConjureBuilding from "../backend/spells/conjuration/ConjureBuilding";
import { clone } from "../utils/CoreUtils";
import {
  LOAD_FROM_FILE_SUPPORTED,
  SAVE_TO_FILE_SUPPORTED,
  saveToFile,
} from "../utils/FilesystemSave";
import { formatNumber } from "../utils/FormattingUtils";
import { ZOOM_SUPPORTED } from "../utils/Zoom";
import { Button } from "./utility/Button";
import CategoryContainer from "./utility/CategoryContainer";
import { CheckBox } from "./utility/CheckBox";
import LoadGameContainer from "./utility/LoadGameContainer";
import { getAllMenuItems } from "./utility/Miscellaneous";
import { Picker as UtilityPicker } from "./utility/Picker";
import { Text } from "./utility/Text";
import { GameIcon } from "./utility/icons/GameIcon";

const FPS_DATA = ["10", "12", "15", "20", "24", "30", "40", "50", "60"].map(
  (x) => ({ label: x, key: x, value: x }),
);

const ZOOM_DATA = [
  0.5, 0.6, 0.7, 0.8, 0.85, 0.9, 0.95, 0.98, 1.0, 1.02, 1.05, 1.1, 1.151, 1.2,
  1.25, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2.0, 2.25, 2.5, 2.75, 3.0,
].map((x) => ({
  label: formatNumber(x, { showDecimals: true }) + "x",
  key: x.toString(),
  value: x.toString(),
}));

function useEventTagToggle(
  playerContextRef: RefObject<PlayerContextValue>,
  tag: EventTag,
) {
  return useCallback(() => {
    playerContextRef.current?.apply((oldState) => {
      if (oldState.global.hideEventTags.includes(tag)) {
        oldState.global.hideEventTags.splice(
          oldState.global.hideEventTags.findIndex(
            (innerTag) => innerTag === tag,
          ),
          1,
        );
      } else {
        oldState.global.hideEventTags.push(tag);
      }
      return oldState;
    });
  }, []);
}

function useHiddenFromBottomMenuToggle(
  playerContextRef: RefObject<PlayerContextValue>,
  section: string,
) {
  return useCallback(() => {
    playerContextRef.current?.apply((oldState) => {
      if (oldState.global.hiddenFromBottomMenu.includes(section)) {
        oldState.global.hiddenFromBottomMenu.splice(
          oldState.global.hiddenFromBottomMenu.findIndex(
            (innerSection) => innerSection === section,
          ),
          1,
        );
      } else {
        oldState.global.hiddenFromBottomMenu.push(section);
      }
      return oldState;
    });
  }, []);
}

function BottomMenuToggle(props: { section: string }) {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;

  const onPress = useHiddenFromBottomMenuToggle(
    playerContextRef,
    props.section,
  );

  const theme = useTheme();

  const title = (
    <View style={styles.toggleMenuRow}>
      <GameIcon icon={props.section.toLowerCase()} small />
      <View style={styles.toggleMenuTextContainer}>
        <Text style={{ color: theme.theme.colors.grey1 }}>{props.section}</Text>
      </View>
    </View>
  );

  return (
    <View>
      <CheckBox
        title={title}
        checked={
          !playerContext.global.hiddenFromBottomMenu.includes(props.section)
        }
        onPress={onPress}
        containerStyle={styles.toggleMenuCheckboxContainer}
      />
    </View>
  );
}

export default function Options() {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;

  const copyToClipboard = useCallback(async () => {
    const serializedData = await serializeSaveData(playerContextRef.current);
    await Clipboard.setStringAsync(serializedData);
  }, []);

  const targetFpsChange = useCallback((value: string) => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.targetFps = Number.parseInt(value);
      return oldState;
    });
  }, []);

  const zoomChange = useCallback((value: string) => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.zoomLevel = Number.parseFloat(value);
      return oldState;
    });
  }, []);

  const elementOverrideChange = useCallback((value: string) => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.overrideElementDisplay = value as SpellElement | "no";
      return oldState;
    });
  }, []);

  const elementOptions = [
    {
      label: "Primary Element",
      key: "no",
      value: "no",
    },
    ...getUnlockedElements(playerContext).map((x) => ({
      label: x,
      key: x,
      value: x,
    })),
  ];

  const hideDamageEffectsToggle = useCallback(() => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.hideDamageEffects = !oldState.global.hideDamageEffects;
      return oldState;
    });
  }, []);

  const doNotUseNativeDriverToggle = useCallback(() => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.doNotUseNativeDriver =
        !oldState.global.doNotUseNativeDriver;
      return oldState;
    });
  }, []);

  const swipingToggle = useCallback(() => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.enableSwiping = !oldState.global.enableSwiping;
      return oldState;
    });
  }, []);

  const darkModeToggle = useCallback(() => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.themeMode =
        oldState.global.themeMode === "dark" ? "light" : "dark";
      return oldState;
    });
  }, []);

  const hideTooltipsToggle = useCallback(() => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.hideTooltips = !oldState.global.hideTooltips;
      return oldState;
    });
  }, []);

  const conjureBuildingAutoOffCallback = useCallback(() => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.automaticallyBuiltBuildingsStartOff =
        !oldState.global.automaticallyBuiltBuildingsStartOff;
      return oldState;
    });
  }, []);

  const wakeLockCallback = useCallback(() => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.wakeLock = !oldState.global.wakeLock;
      return oldState;
    });
  }, []);

  const enableBottomMenuCallback = useCallback(() => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.enableBottomMenu = !oldState.global.enableBottomMenu;
      return oldState;
    });
  }, []);

  const leftHandedModeCallback = useCallback(() => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.leftHandedMode = !oldState.global.leftHandedMode;
      return oldState;
    });
  }, []);

  const putCastSpellOnBottomMenuCallback = useCallback(() => {
    playerContextRef.current.apply((oldState) => {
      oldState.global.putCastSpellOnBottomMenu =
        !oldState.global.putCastSpellOnBottomMenu;
      return oldState;
    });
  }, []);

  const eventTagLevelUpFifteenMinutesToggle = useEventTagToggle(
    playerContextRef,
    EventTag.LEVEL_UP_FIRST_FIFTEEN_MINS,
  );
  const eventTagLevelUpEmptyToggle = useEventTagToggle(
    playerContextRef,
    EventTag.LEVEL_UP_EMPTY,
  );
  const eventTagLevelUpAllToggle = useEventTagToggle(
    playerContextRef,
    EventTag.LEVEL_UP,
  );
  const eventTagInconsequentialRandomEventsToggle = useEventTagToggle(
    playerContextRef,
    EventTag.INCONSEQUENTIAL_RANDOM_EVENT,
  );
  const eventTagEarlyGameEventsToggle = useEventTagToggle(
    playerContextRef,
    EventTag.EARLY_GAME,
  );

  const [eraseText, setEraseText] = useState("");
  const eraseGame = useCallback(() => {
    playerContextRef.current.apply((state) => {
      return clone(PLAYER_INITIAL_STATE);
    });
    AsyncStorage.removeItem("PlayerContextState");
    AsyncStorage.removeItem("LastSaveSecondsPlayed");
  }, []);

  const theme = useTheme();

  const loadText = LOAD_FROM_FILE_SUPPORTED
    ? "You may import save data by loading it from a file, or by pasting it here."
    : "You may import save data by pasting it here.";

  const saveToFileCallback = useCallback(async () => {
    const result = await saveToFile(playerContextRef.current);
  }, []);

  const { width, height } = useWindowDimensions();

  const bottomMenuToggles = useMemo(
    () =>
      getAllMenuItems(playerContext).map((section) => (
        <BottomMenuToggle key={section} section={section} />
      )),
    [getAllMenuItems(playerContext).join()],
  );

  const labelColor = useMemo(
    () => ({ color: theme.theme.colors.grey1 }),
    [theme],
  );

  const performanceCategory = useMemo(() => {
    return (
      <CategoryContainer title="Performance">
        <View style={styles.categoryContainer}>
          <Text>
            You may set your target UI updates per second (UUPS) here. Higher
            UUPS will make the game elements refresh quicker but it could affect
            battery power and CPU usage.
          </Text>
          <View style={styles.targetFpsContainer}>
            <Text style={labelColor}>Target UUPS:</Text>
            <View style={styles.targetFpsDropdownContainer}>
              <UtilityPicker
                onValueChange={targetFpsChange}
                selectedValue={playerContext.global.targetFps.toString()}
                style={styles.targetFpsPicker}
                data={FPS_DATA}
              />
            </View>
          </View>
          {hasFlag(playerContext, "exploration_unlocked") && (
            <View style={styles.hideDamageEffectsContainer}>
              <Text>
                If you are seeing performance problems specifically in the
                Exploration screen, you may turn off damage effects here, which
                should improve performance somewhat. The alternate animation
                code for the bars is less smooth, and may or may not improve
                performance; you may want to try both.
              </Text>
              <View>
                <View>
                  <CheckBox
                    title="Hide damage effects"
                    checked={playerContext.global.hideDamageEffects}
                    onPress={hideDamageEffectsToggle}
                    textStyle={styles.checkboxText}
                    containerStyle={styles.checkboxContainer}
                  />
                </View>
                <View>
                  <CheckBox
                    title="Use alternate animation code for the bars"
                    checked={playerContext.global.doNotUseNativeDriver}
                    onPress={doNotUseNativeDriverToggle}
                    textStyle={styles.checkboxText}
                    containerStyle={styles.checkboxContainer}
                  />
                </View>
              </View>
            </View>
          )}
        </View>
      </CategoryContainer>
    );
  }, [
    doNotUseNativeDriverToggle,
    playerContext.global.doNotUseNativeDriver,
    hideDamageEffectsToggle,
    playerContext.global.hideDamageEffects,
    hasFlag(playerContext, "exploration_unlocked"),
    targetFpsChange,
    playerContext.global.targetFps.toString(),
    FPS_DATA,
    labelColor,
  ]);

  const keepAwakeCategory = useMemo(
    () => (
      <CategoryContainer title="Keep Awake">
        <View style={styles.categoryContainer}>
          <Text>
            By default, devices go to sleep after a period of inactivity, to
            save battery. You can disable this behavior while you are playing
            Magic Research actively in the foreground.
          </Text>
          <View style={styles.targetFpsContainer}>
            <View>
              <CheckBox
                title="Prevent device from sleeping"
                checked={playerContext.global.wakeLock}
                onPress={wakeLockCallback}
                textStyle={styles.checkboxText}
                containerStyle={styles.checkboxContainer}
              />
            </View>
          </View>
        </View>
      </CategoryContainer>
    ),
    [playerContext.global.wakeLock, wakeLockCallback],
  );

  const hideEventPopupsCategory = useMemo(
    () => (
      <CategoryContainer title="Hide Event Popups">
        <View style={styles.categoryContainer}>
          <Text>
            Hide certain kinds of events, so that they don't disrupt gameplay.
            Hidden events will not show a popup, but will still be accessible
            through the Event Menu.
          </Text>
          <View style={styles.eventPopupsContainer}>
            {playerContext.global.totalTimesResetted > 0 && (
              <View>
                <CheckBox
                  title="Hide game progression events that require no action, up to unlocking Exploration"
                  checked={playerContext.global.hideEventTags.includes(
                    EventTag.EARLY_GAME,
                  )}
                  onPress={eventTagEarlyGameEventsToggle}
                  textStyle={styles.checkboxText}
                  containerStyle={styles.checkboxContainer}
                />
              </View>
            )}
            <View>
              <CheckBox
                title="Hide school level up events during the first 15 minutes of a run"
                checked={playerContext.global.hideEventTags.includes(
                  EventTag.LEVEL_UP_FIRST_FIFTEEN_MINS,
                )}
                onPress={eventTagLevelUpFifteenMinutesToggle}
                textStyle={styles.checkboxText}
                containerStyle={styles.checkboxContainer}
              />
            </View>
            <View>
              <CheckBox
                title="Hide school level up events if nothing new is unlocked"
                checked={playerContext.global.hideEventTags.includes(
                  EventTag.LEVEL_UP_EMPTY,
                )}
                onPress={eventTagLevelUpEmptyToggle}
                textStyle={styles.checkboxText}
                containerStyle={styles.checkboxContainer}
              />
            </View>
            <View>
              <CheckBox
                title="Hide all school level up events"
                checked={playerContext.global.hideEventTags.includes(
                  EventTag.LEVEL_UP,
                )}
                onPress={eventTagLevelUpAllToggle}
                textStyle={styles.checkboxText}
                containerStyle={styles.checkboxContainer}
              />
            </View>
            <View>
              <CheckBox
                title="Hide common random events (random events which are not Storylines and which appear more than once)"
                checked={playerContext.global.hideEventTags.includes(
                  EventTag.INCONSEQUENTIAL_RANDOM_EVENT,
                )}
                onPress={eventTagInconsequentialRandomEventsToggle}
                textStyle={styles.checkboxText}
                containerStyle={styles.checkboxContainer}
              />
            </View>
          </View>
        </View>
      </CategoryContainer>
    ),
    [
      playerContext.global.hideEventTags.join(),
      eventTagInconsequentialRandomEventsToggle,
      eventTagLevelUpAllToggle,
      eventTagLevelUpEmptyToggle,
      eventTagLevelUpFifteenMinutesToggle,
      eventTagEarlyGameEventsToggle,
      playerContext.global.totalTimesResetted,
    ],
  );

  const swipeToOpenCategory = useMemo(
    () =>
      width < 960 && (
        <CategoryContainer title="Swipe to Open Menus">
          <View style={styles.categoryContainer}>
            <Text>
              In smaller screens, you can swipe the screen left or right in
              order to open menus. You can disable this behavior here.
            </Text>
            <View style={styles.targetFpsContainer}>
              <CheckBox
                title="Enable swiping"
                checked={playerContext.global.enableSwiping}
                onPress={swipingToggle}
                textStyle={styles.checkboxText}
                containerStyle={styles.checkboxContainer}
              />
            </View>
          </View>
        </CategoryContainer>
      ),
    [playerContext.global.enableSwiping, width, swipingToggle],
  );

  const bottomMenuCategory = useMemo(
    () =>
      width < 700 && (
        <CategoryContainer title="Bottom Menu">
          <View style={styles.categoryContainer}>
            <Text>
              In smaller screens, there is a menu at the bottom to quickly
              switch between screens. You can customize the menu in this
              section. If you are left handed, you can swap the position of the
              Cast Spell button to be able to use it more easily. If you prefer
              to have the extra screen space instead, you can also disable this
              menu here.
            </Text>
            <View style={styles.targetFpsContainer}>
              <CheckBox
                title="Enable bottom menu"
                checked={playerContext.global.enableBottomMenu}
                onPress={enableBottomMenuCallback}
                textStyle={styles.checkboxText}
                containerStyle={styles.checkboxContainer}
              />
            </View>
            <View>
              <CheckBox
                title="Show Cast Spell button in bottom menu"
                checked={playerContext.global.putCastSpellOnBottomMenu}
                onPress={putCastSpellOnBottomMenuCallback}
                textStyle={styles.checkboxText}
                containerStyle={styles.checkboxContainer}
              />
            </View>
            <View>
              <CheckBox
                title="Left handed mode"
                checked={playerContext.global.leftHandedMode}
                onPress={leftHandedModeCallback}
                textStyle={styles.checkboxText}
                containerStyle={styles.checkboxContainer}
              />
            </View>
            <View style={styles.bottomMenuCustomizationContainer}>
              <Text>
                You can also customize which screens will show up on the bottom
                menu. You will always be able to navigate to these screens by
                using the main menu on the left hand side. The bottom menu will
                show the following screens:
              </Text>
              <View style={styles.hideBottomMenuContainer}>
                {bottomMenuToggles}
              </View>
            </View>
          </View>
        </CategoryContainer>
      ),
    [
      width,
      playerContext.global.enableBottomMenu,
      playerContext.global.putCastSpellOnBottomMenu,
      playerContext.global.leftHandedMode,
      enableBottomMenuCallback,
      putCastSpellOnBottomMenuCallback,
      leftHandedModeCallback,
      bottomMenuToggles,
    ],
  );

  const conjureBuildingCategory = useMemo(
    () =>
      ConjureBuilding.isVisible(playerContext) && (
        <CategoryContainer title="Conjure Building">
          <View style={styles.categoryContainer}>
            <Text>
              When the spell Conjure Building is cast automatically, buildings
              that consume resources to operate (such as Furnaces) can be turned
              off by default to prevent extra resource consumption.
            </Text>
            <View style={styles.eventPopupsContainer}>
              <CheckBox
                title="Turn off buildings when automatically created by Conjure Building"
                checked={
                  playerContext.global.automaticallyBuiltBuildingsStartOff
                }
                onPress={conjureBuildingAutoOffCallback}
                textStyle={styles.checkboxText}
                containerStyle={styles.checkboxContainer}
              />
            </View>
          </View>
        </CategoryContainer>
      ),
    [
      playerContext.global.automaticallyBuiltBuildingsStartOff,
      ConjureBuilding.isVisible(playerContext),
      conjureBuildingAutoOffCallback,
    ],
  );

  const themingAndUICategory = useMemo(
    () => (
      <CategoryContainer title="Theming and UI">
        <View style={styles.categoryContainer}>
          <Text>Change the colors to be easier on the eyes.</Text>
          <View style={styles.targetFpsContainer}>
            <CheckBox
              title="Enable dark mode"
              checked={playerContext.global.themeMode === "dark"}
              onPress={darkModeToggle}
              textStyle={styles.checkboxText}
              containerStyle={styles.checkboxContainer}
            />
          </View>
          {Platform.OS === "web" && (
            <View style={styles.elementOverrideContainer}>
              <Text>You can disable tooltips if they get in the way.</Text>
              <View style={styles.targetFpsContainer}>
                <CheckBox
                  title="Disable tooltips"
                  checked={playerContext.global.hideTooltips}
                  onPress={hideTooltipsToggle}
                  textStyle={styles.checkboxText}
                  containerStyle={styles.checkboxContainer}
                />
              </View>
            </View>
          )}
          <View style={styles.elementOverrideContainer}>
            <Text>
              By default, the game changes the colors of the buttons and
              interface depending on the chosen element. If you'd like, you can
              instead choose to always show buttons with the looks of the
              element of your choice.
            </Text>
            <View style={styles.targetFpsContainer}>
              <Text style={labelColor}>Game UI color:</Text>
              <View style={styles.targetFpsDropdownContainer}>
                <UtilityPicker
                  onValueChange={elementOverrideChange}
                  selectedValue={playerContext.global.overrideElementDisplay}
                  style={styles.uiColorPicker}
                  data={elementOptions}
                />
              </View>
            </View>
          </View>
          {ZOOM_SUPPORTED && (
            <View style={styles.elementOverrideContainer}>
              <Text>You can also adjust the zoom level on the game.</Text>
              <View style={styles.targetFpsContainer}>
                <Text style={labelColor}>Zoom scale:</Text>
                <View style={styles.targetFpsDropdownContainer}>
                  <UtilityPicker
                    onValueChange={zoomChange}
                    selectedValue={playerContext.global.zoomLevel.toString()}
                    style={styles.uiColorPicker}
                    data={ZOOM_DATA}
                  />
                </View>
              </View>
            </View>
          )}
        </View>
      </CategoryContainer>
    ),
    [
      playerContext.global.themeMode,
      darkModeToggle,
      labelColor,
      playerContext.global.overrideElementDisplay,
      elementOverrideChange,
      ZOOM_SUPPORTED,
      playerContext.global.zoomLevel,
      zoomChange,
      ZOOM_DATA,
      playerContext.global.hideTooltips,
      hideTooltipsToggle,
    ],
  );

  const saveFileCategory = useMemo(
    () => (
      <CategoryContainer title="Save File">
        <View style={styles.categoryContainer}>
          <Text>
            You may persist your save data by copying it to the clipboard and
            storing it anywhere you'd like.
          </Text>
          <Button
            onPress={copyToClipboard}
            title="Copy savefile to clipboard"
            containerStyle={styles.saveButton}
          />
        </View>
        {SAVE_TO_FILE_SUPPORTED && (
          <View style={styles.categoryContainer}>
            <Text>
              You can also save your data directly to a file or elsewhere.
            </Text>
            <Button
              onPress={saveToFileCallback}
              title="Save to file"
              containerStyle={styles.saveButton}
            />
          </View>
        )}
      </CategoryContainer>
    ),
    [copyToClipboard, SAVE_TO_FILE_SUPPORTED, saveToFileCallback],
  );

  const loadFileCategory = useMemo(
    () => (
      <CategoryContainer title="Load File">
        <View style={styles.categoryContainer}>
          <Text>{loadText}</Text>
          <Text style={{ fontWeight: "bold", color: theme.theme.colors.error }}>
            WARNING: This will erase your current save! Make sure you have
            backed up your data!
          </Text>
          <LoadGameContainer />
        </View>
      </CategoryContainer>
    ),
    [theme.theme.colors.error, loadText],
  );

  const eraseSavedDataCategory = useMemo(
    () => (
      <CategoryContainer title="Erase Saved Data">
        <View style={styles.categoryContainer}>
          <Text>
            If you want to start all over again and retirement isn't enough, you
            can delete your save data entirely.
          </Text>
          <Text style={{ fontWeight: "bold", color: theme.theme.colors.error }}>
            WARNING: This will erase your current save, including all your
            progress with Storylines and retirement! Make sure you have backed
            up your data! Once you have done this, there is no going back! If
            you are sure you want to do this, write "erase" in the text field
            below.
          </Text>
          <Input
            inputContainerStyle={styles.inputContainer}
            value={eraseText}
            onChangeText={setEraseText}
          />
          <Button
            disabled={eraseText.toLowerCase() != "erase"}
            onPress={eraseGame}
            color="error"
            title="Permanently erase all saved data"
          />
        </View>
      </CategoryContainer>
    ),
    [eraseText, eraseGame, setEraseText, eraseText, theme.theme.colors.error],
  );

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <Text h2>Options</Text>
      </View>
      {performanceCategory}
      {keepAwakeCategory}
      {hideEventPopupsCategory}
      {swipeToOpenCategory}
      {bottomMenuCategory}
      {conjureBuildingCategory}
      {themingAndUICategory}
      {saveFileCategory}
      {loadFileCategory}
      {eraseSavedDataCategory}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
  },
  headerContainer: {
    marginBottom: 16,
  },
  categoryContainer: {
    marginTop: 8,
    paddingHorizontal: 8,
  },
  targetFpsContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
  },
  eventPopupsContainer: {
    marginTop: 8,
  },
  targetFpsDropdownContainer: {
    ...Platform.select({
      android: { backgroundColor: "#eee", borderRadius: 10, default: {} },
    }),
    marginLeft: 16,
  },
  targetFpsPicker: {
    width: 100,
  },
  uiColorPicker: {
    width: 160,
  },
  sliderContainer: {
    width: 200,
    marginLeft: 16,
  },
  sliderTextContainer: {
    width: 120,
  },
  checkboxContainer: {
    marginLeft: 0,
    marginTop: 8,
    padding: 0,
  },
  checkboxText: {
    fontWeight: "normal",
  },
  saveButton: {
    marginTop: 8,
  },
  loadMessageContainer: {
    marginBottom: 8,
  },
  inputContainer: {
    borderWidth: 1,
    marginTop: 16,
    maxHeight: 48,
  },
  toggleMenuCheckboxContainer: {
    padding: 0,
    marginLeft: 0,
  },
  toggleMenuRow: {
    marginTop: 8,
    flexDirection: "row",
    alignItems: "center",
  },
  hideBottomMenuContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  toggleMenuTextContainer: {
    paddingLeft: 4,
  },
  bottomMenuCustomizationContainer: {
    marginTop: 8,
  },
  hideDamageEffectsContainer: {
    marginTop: 8,
  },
  elementOverrideContainer: {
    marginTop: 8,
  },
});
